import { useTranslation } from "react-i18next";
import {
  AlertDialog, AlertDialogContent, AlertDialogTitle, AlertDialogDescription,
  AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";

type Props = {
  open: boolean;
  memberName: string;
  pending: boolean;
  error: string;
  onCancel: () => void;
  onConfirm: () => void;
};

export default function ProjectRemoveMemberDialog({ open, memberName, pending, error, onCancel, onConfirm }: Props) {
  const { t, i18n } = useTranslation();
  return (
    <AlertDialog open={open} onOpenChange={(next) => { if (!next && !pending) onCancel(); }}>
      <AlertDialogContent dir={i18n.dir()}
        className="flex max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden p-0"
        onCloseAutoFocus={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => { if (pending) event.preventDefault(); }}>
        <header className="shrink-0 border-b p-4">
          <AlertDialogTitle>{t("projects.confirmMemberRemoval")}</AlertDialogTitle>
        </header>
        <div className="min-h-0 space-y-4 overflow-y-auto p-4">
          <AlertDialogDescription asChild>
            <div className="space-y-3 break-words">
              <p>{t("projects.revokeMemberAccessWarning")}</p>
              <p>{t("projects.removeMemberConfirm", { name: memberName })}</p>
            </div>
          </AlertDialogDescription>
          {error ? <p role="alert" className="text-sm text-destructive dark:text-destructive-foreground">{error}</p> : null}
        </div>
        <footer className="flex shrink-0 flex-wrap justify-end gap-2 border-t p-4">
          <AlertDialogCancel asChild>
            <Button type="button" variant="outline" disabled={pending}>{t("common.cancel")}</Button>
          </AlertDialogCancel>
          <AlertDialogAction asChild onClick={(event) => { event.preventDefault(); if (!pending) onConfirm(); }}>
            <Button type="button" variant="destructive" disabled={pending}>
              {pending ? <Spinner className="size-4 motion-reduce:animate-none" /> : null}
              {pending ? t("common.removing") : t("common.remove")}
            </Button>
          </AlertDialogAction>
        </footer>
      </AlertDialogContent>
    </AlertDialog>
  );
}
